import type React from 'react';

type AnswerValue = string | number | string[] | null | undefined | Record<string, unknown>;
type AnswerMap = Record<string, AnswerValue>;

export type LivePreviewRenderer = (
  item: any,
  value: AnswerValue,
  set: (v: AnswerValue) => void,
) => React.ReactNode | null;

export type CanvasPreviewRenderer = (item: any) => React.ReactNode;

export interface NaomiPluginRegistry {
  ensureChoiceList: ((sdm: any, itemOid: string) => any) | null;
  evaluateValidation: ((value: AnswerValue, validations: any, dataType: string) => string | null) | null;
  renderGroupHeaderBanner: ((group: any) => React.ReactNode | null) | null;
  fileToObjectURL: ((file: File) => string | null) | null;
  repeatCountFromAnswers: ((group: any, answers: AnswerMap) => number | null) | null;
  GroupHeaderImageDialog: React.ComponentType<any> | null;
  ChoiceOptionImageInput: React.ComponentType<any> | null;
  ImageDropPicker: React.ComponentType<any> | null;
  RenderValidationError: React.ComponentType<any> | null;
  IconChoicePreview: React.ComponentType<any> | null;
  ImageConstraintsEditor: React.ComponentType<any> | null;
  CustomTypeBuilder: React.ComponentType<any> | null;
  RepeatingGroupConfig: React.ComponentType<any> | null;
  RichVersionsPane: React.ComponentType<any> | null;
  livePreviewExtras: Record<string, LivePreviewRenderer>;
  canvasPreviewExtras: Record<string, CanvasPreviewRenderer>;
  customTypes: Record<string, any>;
}

const registry: NaomiPluginRegistry = {
  ensureChoiceList: null,
  evaluateValidation: null,
  renderGroupHeaderBanner: null,
  fileToObjectURL: null,
  repeatCountFromAnswers: null,
  GroupHeaderImageDialog: null,
  ChoiceOptionImageInput: null,
  ImageDropPicker: null,
  RenderValidationError: null,
  IconChoicePreview: null,
  ImageConstraintsEditor: null,
  CustomTypeBuilder: null,
  RepeatingGroupConfig: null,
  RichVersionsPane: null,
  livePreviewExtras: {},
  canvasPreviewExtras: {},
  customTypes: {},
};

type PluginFields = Omit<NaomiPluginRegistry, 'livePreviewExtras' | 'canvasPreviewExtras' | 'customTypes'>;

export function registerNaomiPlugins(plugins: Partial<PluginFields>): void {
  Object.assign(registry, plugins);
}

export function registerLivePreviewExtra(dataType: string, renderer: LivePreviewRenderer): void {
  registry.livePreviewExtras[dataType] = renderer;
}

export function registerCanvasPreviewExtra(dataType: string, renderer: CanvasPreviewRenderer): void {
  registry.canvasPreviewExtras[dataType] = renderer;
}

export function setNaomiCustomTypes(types: Record<string, any>): void {
  registry.customTypes = types;
}

export function getNaomiRegistry(): Readonly<NaomiPluginRegistry> {
  return registry;
}
